// Hand-rolled bridge from specs/seeds/dashboard-v2/wizards/import-map.yaml
// (CHG-1241 / ITER-3364). See campaign-create.ts header for rationale.
//
// Special: declares 2 custom_renders requiring registerWizardComponent() at
// runtime. ImportColumnMappingTable + ImportPreviewTable ship as stubs in
// this CHG (marked data-wizard-stub="true"); a future CHG ships the real
// components.

import type { WizardManifest } from "../../wizards/manifestTypes";

export const importMapManifest: WizardManifest = {
  wizard_id: "import-map",
  version: "1.0.0",
  spec_id: "experience.cross-screen.import-map-v2",
  title: "Import data",
  subtitle: "Map columns from a CSV / XLSX and execute the import in 4 steps.",
  shape: "modal-wizard",
  size: "lg",
  persistence_key: "import-map",
  trigger: {
    permission: "alora-giving.import.create",
    invocation: [{ from_route: "/imports", button: "New import" }],
  },
  field_type_coverage: {
    canonical: ["file_upload", "datetime", "email"],
    pending_primitive: ["text"],
    custom_renders: [
      {
        step_id: "map",
        component_id: "ImportColumnMappingTable",
        reason: "Per-row column → entity-field mapping with auto-detect from canonical _HEADER_PATTERNS contract.",
      },
      {
        step_id: "preview",
        component_id: "ImportPreviewTable",
        reason: "Read-only preview of first 10 rows + validation counts (projected from /imports/{id}/summary).",
      },
    ],
  },
  steps: [
    {
      step_id: "upload",
      label: "Upload",
      description: "Pick the source file and entity type.",
      fields: [
        {
          name: "source_file",
          type: "file_upload",
          label: "Source file",
          required: true,
          props: {
            accept:
              "text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        },
        {
          name: "entity_type",
          type: "text",
          label: "Entity type",
          required: true,
          pending_primitive: true,
          props: { enum: ["donor", "donation", "pledge", "recurring_donation", "prospect"] },
        },
      ],
    },
    {
      step_id: "map",
      label: "Map columns",
      description:
        "Match each source column to a canonical entity field. Auto-detected via _HEADER_PATTERNS where possible.",
      fields: [
        {
          name: "column_mapping",
          type: "text",
          label: "Column mapping",
          required: true,
          pending_primitive: true,
          props: { rendered_by_component: "ImportColumnMappingTable", opaque_json: true },
        },
      ],
    },
    {
      step_id: "preview",
      label: "Preview",
      description: "Review the first 10 rows + validation counts before executing.",
      // YAML manifest declares fields:[] for this step + a step-level custom
      // render via field_type_coverage.custom_renders. The TS bridge injects a
      // synthetic field so FormDialog's per-field renderer surfaces the
      // ImportPreviewTable custom component. Documented deviation from the
      // YAML; reconciles at the next FE-generator CHG.
      fields: [
        {
          name: "__preview",
          type: "text",
          pending_primitive: true,
          props: { rendered_by_component: "ImportPreviewTable" },
        },
      ],
    },
    {
      step_id: "schedule",
      label: "Confirm + schedule",
      description: "Run now or schedule for later; choose who gets notified.",
      fields: [
        { name: "run_at", type: "datetime", label: "Execute at (leave blank to run now)" },
        { name: "notify_email", type: "email", label: "Notify on completion" },
      ],
    },
  ],
  on_submit: {
    api_endpoint: "POST /imports/{import_batch_id}/execute",
    on_success: { redirect: "/imports/{response.id}" },
    on_error: { show_in_step: true },
  },
  validation: [
    { step_id: "upload", field: "source_file", rule: "not_empty" },
    { step_id: "upload", field: "entity_type", rule: "enum_member" },
    { step_id: "map", field: "column_mapping", rule: "not_empty" },
    { step_id: "map", field: "column_mapping", rule: "all_required_target_fields_mapped" },
    { step_id: "schedule", field: "run_at", rule: "not_in_past_when_set" },
    { step_id: "schedule", field: "notify_email", rule: "email_format_or_empty" },
  ],
};
